/**
 * Split-/Merge-Hinweis im Drawer (Task 11) - liest `lineageLaden` (Task 8, `api/lineage.ts`) und
 * zeigt, falls vorhanden, den Zusammenhang zur Ursprungs- bzw. Zielbestellung. Reine Anzeige, kein
 * eigener Schreibweg. Task 12 bindet diese Komponente in den Drawer ein (`Drawer.tsx`, direkt neben
 * `DrawerKopf` - AUSSERHALB von `.drawer-body`, siehe Kommentar dort, Pflichtreview-Befund 2).
 *
 * Fix-Runde (Pflichtreview zu Commit d04869f7, siehe Plan-Korrektur 8d67336d): die ursprüngliche
 * Fassung hatte einen Kritisch-Befund (doppeltes Präfix - `zeile()` setzte "gesplittet von "/
 * "entstanden durch..." selbst UND der `asSource`-Aufrufer setzte "aufgeteilt in "/"aufgegangen
 * in " nochmal davor, ergab z. B. "aufgeteilt in gesplittet von o3") sowie zwei Wichtig-Befunde
 * (Merge-Ziel zeigte nur `quellen[0]`, Popover-`<table>` als Kind eines `<button>` verstieß gegen
 * das HTML-Inhaltsmodell und ein Klick in die Tabelle schloss das Popover sofort wieder). Diese
 * Fassung trennt deshalb `BestellVerweis` (ein Zielverweis) und `LineageZeile` (Präfix + eine oder
 * mehrere Verweise + eigener Popover-Umschalter NEBEN statt umschließend den Verweisen).
 *
 * Fix-Runde 2 (Pflichtreview zu Commit `5e27997c`, Task 12): drei weitere Wichtig-Befunde. Neben der
 * Einbindungsstelle (oben) und dem Nachladeweg fuer nicht im Store stehende Bestellungen
 * (`App.tsx#lineageOeffnen`) zeigt ein Verweis seither die lesbare Bestellnummer (`beschriftung`
 * unten) statt der rohen `orderId` - siehe `LineageHinweisProps.bestellungen`.
 *
 * Endabnahme-Fixrunde (Pflichtreview, letzte Fixwelle vor dem Zusammenfuehren): zwei weitere
 * Aenderungen. `LineageHinweisProps.lineageVersion` (siehe dort) erzwingt ein Nachladen nach einem
 * erfolgreichen Split im offen bleibenden Quell-Drawer - vorher blieb die Komponente dort auf ihrem
 * alten, leeren Stand stehen, weil `orderId` sich beim Splitten nicht aendert. Ausserdem: eigene CSS-
 * Datei `styles/neubau/lineage.css` (vorher ungestylt) - siehe deren Kopfkommentar fuer die
 * Begruendung, warum das erst jetzt statt bei Task 11/12 nachgezogen wurde.
 */
import { useEffect, useState, type JSX } from "react";
import {
  lineageLaden,
  type Lineage,
  type LineageEreignis,
  type LineageRueckfuehrungsEreignis,
} from "../../api/lineage.ts";
import { kennungVon } from "../../lib/bestellkennung.ts";
import { NUM, fmtDate } from "../../lib/format.ts";
import type { Order } from "../../lib/types.ts";
import { meldeFehler } from "../../state/fehlerStapel.ts";

export interface LineageHinweisProps {
  orderId: string;
  /** Navigiert zur verlinkten Bestellung (gleiches Muster wie Historie.tsx#onOeffnen). */
  onOeffnen: (orderId: string) => void;
  /**
   * Task 12 (Pflichtreview-Befund 3 zu Commit `5e27997c`): `DrawerKontext.bestellungen`, durchgereicht
   * fuer die lesbare Beschriftung eines Verweises (`kennungVon`) statt der rohen `orderId` (UUID).
   * Findet sich eine verlinkte `orderId` HIER NICHT (z. B. eine archivierte oder frisch gesplittete
   * Bestellung, die der Archivfilter aus dem Store haelt), bleibt die rohe id der Fallback-Text - der
   * Klick funktioniert trotzdem (siehe `App.tsx#lineageOeffnen`, Nachladeweg).
   */
  bestellungen: readonly Order[];
  /**
   * Endabnahme-Fixrunde (Pflichtreview-Befund 2): reiner Zaehler, siehe Kopfkommentar
   * `App.tsx#lineageVersion` fuer die vollstaendige Begruendung/Bauart. Nur als zusaetzliche
   * Abhaengigkeit fuer den Ladeeffekt unten gedacht - der Wert selbst traegt keine Bedeutung, nur
   * eine AENDERUNG zaehlt. Optional, damit bestehende Aufrufer (Tests) ihn nicht mitgeben muessen.
   */
  lineageVersion?: number;
}

/** Die lesbare Beschriftung eines Verweises, mit Rueckfall auf die rohe id ohne Treffer im Store. */
function beschriftung(orderId: string, bestellungen: readonly Order[]): string {
  const treffer = bestellungen.find((b) => String(b.id) === orderId);
  return treffer ? kennungVon(treffer, orderId) : orderId;
}

/**
 * Ein einzelner klickbarer Verweis auf eine andere Bestellung - eigenes `<button>`, damit ein
 * Klick darauf nie den umgebenden Popover-Umschalter auslöst UND per Tastatur erreichbar ist
 * (kein `role="link"` auf einem `<span>` - das wäre für Tastaturnutzer unerreichbar).
 *
 * Zeigt die lesbare Bestellnummer (`lib/bestellkennung.ts#kennungVon`), falls die Bestellung im
 * Store steht, sonst die rohe `orderId` als Rueckfall (`beschriftung` oben) - der `onOeffnen`-Aufruf
 * unten bekommt IMMER die rohe `orderId`, unabhaengig von der Beschriftung.
 */
function BestellVerweis({
  orderId, bestellungen, onOeffnen,
}: { orderId: string; bestellungen: readonly Order[]; onOeffnen: (orderId: string) => void }): JSX.Element {
  return (
    <button
      type="button"
      className="lineage-hinweis-verweis"
      onClick={(event) => {
        event.stopPropagation();
        onOeffnen(orderId);
      }}
    >
      {beschriftung(orderId, bestellungen)}
    </button>
  );
}

/**
 * Eine Zeile der Historie: Präfixtext + ein oder mehrere klickbare Zielverweise (Komma-getrennt),
 * daneben ein eigener Umschalter für das Mengen-/Optionen-Popover. Der Präfix kommt ausschließlich
 * von hier aus dem Aufrufer (`praefix`-Prop) - NICHT zusätzlich aus dieser Komponente selbst, sonst
 * verdoppelt er sich beim Aufruf für die Quellseite (Pruefungsbefund, Kritisch: eine frühere
 * Fassung setzte "gesplittet von "/"entstanden durch..." hier UND "aufgeteilt in "/"aufgegangen
 * in " beim Aufrufer davor - ergab "aufgeteilt in gesplittet von X").
 *
 * Der Popover-Umschalter sitzt bewusst NEBEN den Verweisen, nicht als umschließendes Element - ein
 * `<table>`-Popover als Kind eines `<button>` verstieße gegen das HTML-Inhaltsmodell, und ein
 * Klick in die Tabelle schlösse das Popover sofort wieder (derselbe frühere Befund).
 *
 * `role="dialog"` OHNE `aria-modal="true"` (Endabnahme-Fixrunde, Pflichtreview-Befund): das Popover
 * ist kein echter Modal-Dialog - es sperrt weder den Fokus (kein Fokus-Trap) noch reagiert es auf
 * Escape. `aria-modal="true"` behauptete Screenreadern faelschlich beides.
 */
function LineageZeile({
  ereignis, praefix, ziele, bestellungen, onOeffnen,
}: {
  ereignis: LineageEreignis;
  praefix: string;
  ziele: readonly string[];
  bestellungen: readonly Order[];
  onOeffnen: (orderId: string) => void;
}): JSX.Element {
  const [offen, setOffen] = useState(false);
  return (
    <div className="lineage-hinweis-zeile">
      <span className="lineage-hinweis-text">
        {praefix}
        {ziele.map((zielId, index) => (
          <span key={zielId}>
            {index > 0 ? ", " : ""}
            <BestellVerweis orderId={zielId} bestellungen={bestellungen} onOeffnen={onOeffnen} />
          </span>
        ))}
      </span>
      <button
        type="button"
        className="lineage-hinweis-toggle"
        aria-expanded={offen}
        onClick={() => setOffen((wert) => !wert)}
      >
        Details
      </button>
      {offen && (
        <div
          className="lineage-hinweis-popover"
          role="dialog"
          aria-label="Mengen der Aufspaltung/Zusammenführung"
        >
          <table>
            <thead>
              <tr><th>SKU</th><th>Vorher</th><th>Verschoben</th><th>Nachher</th></tr>
            </thead>
            <tbody>
              {ereignis.quellen.flatMap((quelle) =>
                quelle.positionen.map((pos) => (
                  <tr key={`${quelle.orderId}-${pos.posId}`}>
                    <td>{pos.sku ?? "—"}</td>
                    <td>{NUM.format(pos.mengeVorher)}</td>
                    <td>{NUM.format(pos.mengeVerschoben)}</td>
                    <td>{NUM.format(pos.mengeNachher)}</td>
                  </tr>
                )),
              )}
            </tbody>
          </table>
          {ereignis.optionen && (
            <div className="lineage-hinweis-optionen">
              Übernommen: {[
                ereignis.optionen.dokumente ? "Dokumente" : null,
                ereignis.optionen.tasks ? "Tasks" : null,
              ].filter(Boolean).join(", ") || "nichts"}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Zeile fuer eine Rueckfuehrung (split_undo) aus `zurueckgefuehrt`. "Split aufgelöst: <kindNummer>
 * zurückgeführt (<Datum>)" NUR bei Stand `abgeschlossen` (die Teillieferung ist geloescht). Vorher
 * heisst es "Rückführung aus <kindNummer> begonnen – noch nicht abgeschlossen": bei `geplant` steht
 * in JTL noch nichts (keine Mengen), bei `aufgefuellt` stehen die Mengen schon dort (mit Mengen).
 * Die Kindbestellung gibt es nicht mehr bzw. sie ist am Kind selbst zu sehen - kein Verweis. Die Mengen
 * kommen aus `optionen.plan` (nicht aus `quellen`, nicht mit der Split-Formel vorher - nachher).
 */
function RueckfuehrungsZeile({ ereignis }: { ereignis: LineageRueckfuehrungsEreignis }): JSX.Element {
  const [offen, setOffen] = useState(false);
  const { stand, kindNummer } = ereignis.optionen;
  const text = stand === "abgeschlossen"
    ? `Split aufgelöst: ${kindNummer} zurückgeführt (${fmtDate(ereignis.createdAt)})`
    : `Rückführung aus ${kindNummer} begonnen – noch nicht abgeschlossen (${fmtDate(ereignis.createdAt)})`;
  const mitMengen = stand !== "geplant";
  return (
    <div className="lineage-hinweis-zeile">
      <span className="lineage-hinweis-text">{text}</span>
      {mitMengen && (
        <button
          type="button"
          className="lineage-hinweis-toggle"
          aria-expanded={offen}
          onClick={() => setOffen((wert) => !wert)}
        >
          Details
        </button>
      )}
      {mitMengen && offen && (
        <div className="lineage-hinweis-popover" role="dialog" aria-label="Mengen der Rückführung">
          <ul>
            {ereignis.optionen.plan.map((zeile, index) => (
              <li key={zeile.kLieferantenBestellungPos ?? `neu-${index}`}>
                {`${zeile.artikelNummer ?? "—"}: ${NUM.format(zeile.mengeZurueck)} zurück (${NUM.format(zeile.mengeVorher)} → ${NUM.format(zeile.mengeNachher)})`}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/**
 * Zeigt, falls vorhanden, den Split-/Merge-Zusammenhang einer Bestellung an - klickbar zur
 * verlinkten Bestellung, mit Popover fuer die Mengen vorher/verschoben/nachher. Zeigt nichts
 * (`null`), wenn die Bestellung an keiner Split-/Merge-Aktion beteiligt war (auch: wenn sie vor
 * dieser Iteration entstanden ist - kein rueckwirkendes Befuellen, siehe Design-Spec).
 */
export function LineageHinweis({
  orderId, onOeffnen, bestellungen, lineageVersion,
}: LineageHinweisProps): JSX.Element | null {
  const [lineage, setLineage] = useState<Lineage | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    setLineage(null);
    lineageLaden(orderId, controller.signal)
      .then(setLineage)
      .catch((fehler: unknown) => {
        // L6/L5: ein Abbruch (Wechsel der Bestellung oder Schliessen, das Aufraeumen unten) ist kein Fehler; ein erst
        // danach gescheiterter Abruf betrifft keine sichtbare Zeile mehr. Kein Eintrag, kein Eingriff in den Stand.
        if (controller.signal.aborted) return;
        setLineage({ asTarget: null, asSource: [] });
        // L2: der Hinweis ist eine Zeile im Kopf des Drawers, ohne eigene Flaeche fuer einen Ladefehler. Ohne diesen
        // Eintrag liesse "kein Hinweis" nicht erkennen, ob die Bestellung nie geteilt wurde oder das Laden scheiterte.
        // Der Satz nennt die Bestellung nicht (L5): gleiche Fehlschlaege verschiedener Drawer laufen im Stapel zusammen.
        meldeFehler(fehler, "Der Hinweis auf Teilung oder Zusammenführung dieser Bestellung konnte nicht geladen werden.");
      });
    return () => controller.abort();
    // `lineageVersion` (Endabnahme-Fixrunde, Pflichtreview-Befund 2): erzwingt ein Nachladen nach
    // einem erfolgreichen Split im offen bleibenden Quell-Drawer, siehe Kopfkommentar der Prop. Der
    // Wert selbst wird hier nicht gelesen - er dient AUSSCHLIESSLICH als zusaetzliche Abhaengigkeit.
  }, [orderId, lineageVersion]);

  const zurueckgefuehrt = lineage?.zurueckgefuehrt ?? [];
  if (!lineage || (!lineage.asTarget && lineage.asSource.length === 0 && zurueckgefuehrt.length === 0)) return null;

  return (
    <div className="lineage-hinweis">
      {lineage.asTarget && (
        <LineageZeile
          ereignis={lineage.asTarget}
          praefix={
            lineage.asTarget.eventType === "split"
              ? "gesplittet von "
              : "entstanden durch Zusammenführung von "
          }
          // Bei Split genau eine Quelle, bei Merge alle - beide werden hier gleich behandelt,
          // keine Sonderfallunterscheidung nötig (Pruefungsbefund: eine frühere Fassung nahm hart
          // `quellen[0]`, das unterschlug bei Merge alle Quellen außer der ersten).
          ziele={lineage.asTarget.quellen.map((quelle) => quelle.orderId)}
          bestellungen={bestellungen}
          onOeffnen={onOeffnen}
        />
      )}
      {zurueckgefuehrt.map((ereignis) => (
        <RueckfuehrungsZeile key={ereignis.id} ereignis={ereignis} />
      ))}
      {lineage.asSource.map((ereignis) =>
        ereignis.eventType === "split_undo" ? (
          // Kind im Teilfehlerzustand: die Rueckfuehrung ist begonnen, aber nicht abgeschlossen
          // (kein "aufgegangen in" - das waere eine Zusammenfuehrung).
          <div key={ereignis.id} className="lineage-hinweis-zeile">
            {`Rückführung in ${ereignis.optionen.quelleNummer} begonnen – noch nicht abgeschlossen`}
          </div>
        ) : ereignis.targetOrderId ? (
          <LineageZeile
            key={ereignis.id}
            ereignis={ereignis}
            praefix={ereignis.eventType === "split" ? "aufgeteilt in " : "aufgegangen in "}
            ziele={[ereignis.targetOrderId]}
            bestellungen={bestellungen}
            onOeffnen={onOeffnen}
          />
        ) : (
          // target_order_id ist ON DELETE SET NULL (Migration 038) - die Zielbestellung wurde
          // gelöscht. Die Design-Spec verlangt hierfür ausdrücklich eine Anzeige, kein stilles
          // Weglassen (Pruefungsbefund).
          <div key={ereignis.id} className="lineage-hinweis-zeile">
            {ereignis.eventType === "split" && ereignis.optionen?.zielNummer
              // Die Rueckfuehrung hat die Nummer vor dem Loeschen am Split vermerkt (Backend: vermerkeZielNummerAmSplit).
              ? `aufgeteilt in ${ereignis.optionen.zielNummer} (inzwischen zurückgeführt)`
              : `${ereignis.eventType === "split" ? "aufgeteilt in " : "aufgegangen in "}Bestellung nicht mehr auffindbar`}
          </div>
        ),
      )}
    </div>
  );
}
